import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/Card';
import { api } from '../lib/api';
import { cn } from '../lib/utils';

type Tab = 'mine' | 'public' | 'global';

export function Leagues() {
  const [tab, setTab] = useState<Tab>('mine');

  return (
    <div className="mx-auto max-w-md px-4 pt-4 pb-24 space-y-4">
      <header className="flex items-baseline justify-between">
        <h1 className="font-display text-3xl uppercase tracking-wide">Leagues</h1>
        <Link
          to="/leagues/new"
          className="font-display-alt text-xs uppercase tracking-widest text-trophy-gold"
        >
          + Create
        </Link>
      </header>

      <div className="grid grid-cols-3 rounded-lg border border-border-base bg-bg-card p-1">
        {(['mine', 'public', 'global'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              'rounded-md py-2 font-display-alt text-xs uppercase tracking-widest transition-colors',
              tab === t ? 'bg-trophy-gold text-bg-night' : 'text-text-dim',
            )}
          >
            {t === 'mine' ? 'My' : t === 'public' ? 'Public' : 'Global'}
          </button>
        ))}
      </div>

      {tab === 'mine' && <MyLeagues />}
      {tab === 'public' && <PublicLeagues />}
      {tab === 'global' && <GlobalLeaderboard />}
    </div>
  );
}

function MyLeagues() {
  const q = useQuery({ queryKey: ['leagues', 'me'], queryFn: () => api.leaguesMe() });
  if (q.isLoading) return <SkeletonList />;
  if (!q.data || q.data.leagues.length === 0) {
    return (
      <Card>
        <div className="text-text-dim text-sm">
          You're not in any leagues. Country leagues unlock at WC kickoff (11 June).
        </div>
      </Card>
    );
  }
  return (
    <div className="space-y-2">
      {q.data.leagues.map(({ league, member }) => (
        <Link
          key={league.id}
          to={`/leagues/${league.slug}`}
          className="block rounded-xl border border-border-base bg-bg-card p-4 active:scale-[0.99]"
        >
          <div className="flex items-center justify-between">
            <span className="font-chunky text-base">{league.name}</span>
            <span className="font-mono text-trophy-gold">{member.totalPoints} pts</span>
          </div>
          <div className="font-display-alt text-xs uppercase tracking-widest text-text-faint mt-1">
            Rank #{member.currentRank ?? '—'} · {league.leagueType}
          </div>
        </Link>
      ))}
    </div>
  );
}

function PublicLeagues() {
  const q = useQuery({ queryKey: ['leagues', 'public'], queryFn: () => api.leaguesPublic() });
  if (q.isLoading) return <SkeletonList />;
  if (!q.data || q.data.leagues.length === 0) {
    return (
      <Card>
        <div className="text-text-dim text-sm">No public leagues yet.</div>
      </Card>
    );
  }
  return (
    <div className="space-y-2">
      {q.data.leagues.map((l) => (
        <Link
          key={l.id}
          to={`/leagues/${l.slug}`}
          className="block rounded-xl border border-border-base bg-bg-card p-4 active:scale-[0.99]"
        >
          <div className="flex items-center justify-between">
            <span className="font-chunky text-base">{l.name}</span>
            <span className="font-mono text-text-dim text-sm">{l.memberCount} members</span>
          </div>
          <div className="font-display-alt text-xs uppercase tracking-widest text-text-faint mt-1">
            {l.leagueType} · {l.countryCode ?? 'World'}
          </div>
        </Link>
      ))}
    </div>
  );
}

function GlobalLeaderboard() {
  const q = useQuery({
    queryKey: ['leaderboard', 'global'],
    queryFn: () => api.leaderboardGlobal('all'),
  });
  if (q.isLoading) return <SkeletonList />;
  if (!q.data || q.data.leaderboard.length === 0) {
    return (
      <Card>
        <div className="text-text-dim text-sm">Leaderboard fills up once predictions are scored.</div>
      </Card>
    );
  }
  return (
    <div className="rounded-xl border border-border-base bg-bg-card overflow-hidden">
      {q.data.leaderboard.map((row, i) => (
        <div
          key={row.id}
          className={cn(
            'flex items-center justify-between px-4 py-2 border-b border-border-base last:border-b-0',
            i < 3 && 'bg-trophy-gold/5',
          )}
        >
          <div className="flex items-center gap-3">
            <span className="font-mono text-text-faint w-6 text-right tabular-nums">{i + 1}</span>
            <span className="font-chunky text-sm">{row.firstName}</span>
            {row.telegramUsername && (
              <span className="font-mono text-xs text-text-faint">@{row.telegramUsername}</span>
            )}
          </div>
          <span className="font-mono text-trophy-gold">{row.totalPoints}</span>
        </div>
      ))}
    </div>
  );
}

function SkeletonList() {
  return (
    <div className="space-y-2 animate-pulse">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-16 rounded-xl bg-bg-card border border-border-base" />
      ))}
    </div>
  );
}
